/**
 * /#/admin/discipline — manager visibility panel.
 *
 * Tabs:
 *   Refusals (last 30 days)
 *   Defended (status='submitted')
 *   Ignored (followup expired without completion)
 *   Overrides (with Approve / Deny for 'requested')
 *   Overdue follow-ups
 *
 * Manager+ only. Server already enforces via requireManager on the GET
 * endpoints; this page calls those endpoints.
 *
 * Mount in App.tsx behind AuthGuard with a manager role check:
 *   <Route path="/admin/discipline">
 *     <AuthGuard requireRole="manager">
 *       <DisciplineAdminPage />
 *     </AuthGuard>
 *   </Route>
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { api } from "@/lib/session";

type TabKey = "refusals" | "defended" | "ignored" | "overrides" | "overdue";

export default function DisciplineAdminPage() {
  const [tab, setTab] = useState<TabKey>("refusals");
  return (
    <div
      style={{
        padding: 16,
        background: "#FAFAF7",
        minHeight: "100vh",
        color: "#0B0B0F",
      }}
    >
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Discipline</h1>
      <p style={{ color: "#666", marginBottom: 16 }}>
        Refusals, defenses, overrides, and overdue follow-ups across your tenant.
      </p>
      <Tabs current={tab} onChange={setTab} />
      <div style={{ marginTop: 16 }}>
        {tab === "refusals" && <RefusalsTab />}
        {tab === "defended" && <DefendedTab />}
        {tab === "ignored" && <IgnoredTab />}
        {tab === "overrides" && <OverridesTab />}
        {tab === "overdue" && <OverdueTab />}
      </div>
    </div>
  );
}

function Tabs({ current, onChange }: { current: TabKey; onChange: (t: TabKey) => void }) {
  const tabs: { key: TabKey; label: string }[] = [
    { key: "refusals", label: "Refusals (30d)" },
    { key: "defended", label: "Defended" },
    { key: "ignored", label: "Ignored" },
    { key: "overrides", label: "Overrides" },
    { key: "overdue", label: "Overdue follow-ups" },
  ];
  return (
    <div
      role="tablist"
      style={{
        display: "flex",
        gap: 4,
        borderBottom: "1px solid #ddd",
        flexWrap: "wrap",
      }}
    >
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          data-testid={`tab-${t.key}`}
          aria-selected={current === t.key}
          onClick={() => onChange(t.key)}
          style={{
            border: 0,
            background: current === t.key ? "#0B0B0F" : "transparent",
            color: current === t.key ? "#FAFAF7" : "#0B0B0F",
            padding: "8px 12px",
            fontWeight: 600,
            cursor: "pointer",
            borderRadius: 6,
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function useDefenses(status?: string) {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api(
        `/api/discipline/defenses${status ? `?status=${encodeURIComponent(status)}` : ""}`,
      );
      const body = await r.json();
      if (!r.ok) throw new Error(body?.error ?? "Failed to load");
      setRows(body.defenses ?? []);
    } catch (e: any) {
      setError(e?.message ?? "Failed");
    } finally {
      setLoading(false);
    }
  }, [status]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { rows, loading, error, reload };
}

function RefusalsTab() {
  const since = useMemo(() => new Date(Date.now() - 30 * 86400_000).toISOString(), []);
  const { rows, loading, error } = useDefenses();
  const recent = rows.filter((r) => r.created_at >= since);
  if (loading) return <Loading />;
  if (error) return <ErrorRow message={error} />;
  if (recent.length === 0) return <Empty message="No refusals in the last 30 days." />;
  return <DefensesTable rows={recent} mode="readonly" />;
}

function DefendedTab() {
  const { rows, loading, error, reload } = useDefenses("submitted");
  if (loading) return <Loading />;
  if (error) return <ErrorRow message={error} />;
  if (rows.length === 0) return <Empty message="No defenses awaiting review." />;
  return <DefensesTable rows={rows} mode="review" onChanged={reload} />;
}

function IgnoredTab() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      const r = await api(`/api/discipline/followups?scope=tenant&status=escalated`);
      const body = await r.json();
      setRows((body?.followups ?? []).filter((f: any) => !f.completed_at));
      setLoading(false);
    })();
  }, []);
  if (loading) return <Loading />;
  if (rows.length === 0) return <Empty message="Nothing ignored. Reps are responding." />;
  return <FollowupsTable rows={rows} />;
}

function OverridesTab() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    setLoading(true);
    const r = await api(`/api/discipline/overrides`);
    const body = await r.json();
    setRows(body.overrides ?? []);
    setLoading(false);
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);
  if (loading) return <Loading />;
  if (rows.length === 0) return <Empty message="No override activity." />;
  return <OverridesTable rows={rows} onChanged={reload} />;
}

function OverdueTab() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      const r = await api(`/api/discipline/followups?scope=tenant&status=open`);
      const body = await r.json();
      const now = Date.now();
      const overdue = (body?.followups ?? []).filter(
        (f: any) => new Date(f.escalate_to_manager_at).getTime() <= now,
      );
      setRows(overdue);
      setLoading(false);
    })();
  }, []);
  if (loading) return <Loading />;
  if (rows.length === 0) return <Empty message="No overdue follow-ups." />;
  return <FollowupsTable rows={rows} />;
}

function DefensesTable({
  rows,
  mode,
  onChanged,
}: {
  rows: any[];
  mode: "readonly" | "review";
  onChanged?: () => void;
}) {
  async function decide(id: string, status: "accepted" | "rejected") {
    await api(`/api/discipline/defenses/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    onChanged?.();
  }
  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <Th>Date</Th>
          <Th>Rule</Th>
          <Th>Rep</Th>
          <Th>Entity</Th>
          <Th>Defense</Th>
          <Th>Status</Th>
          {mode === "review" && <Th>Action</Th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <Td>{new Date(r.created_at).toLocaleString()}</Td>
            <Td>{r.rule_code}</Td>
            <Td>{r.user_id?.slice(0, 8) ?? "—"}</Td>
            <Td>
              <a
                href={
                  r.entity_kind === "account"
                    ? `#/accounts/${r.entity_id}`
                    : r.entity_kind === "opportunity"
                    ? `#/opportunities/${r.entity_id}`
                    : "#"
                }
              >
                {r.entity_kind}/{r.entity_id}
              </a>
            </Td>
            <Td>{r.defense_text}</Td>
            <Td>{r.status}</Td>
            {mode === "review" && (
              <Td>
                <button
                  data-testid={`accept-defense-${r.id}`}
                  onClick={() => decide(r.id, "accepted")}
                  style={btnPrimary}
                >
                  Accept
                </button>
                <button
                  data-testid={`reject-defense-${r.id}`}
                  onClick={() => decide(r.id, "rejected")}
                  style={btnSecondary}
                >
                  Reject
                </button>
              </Td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function OverridesTable({ rows, onChanged }: { rows: any[]; onChanged: () => void }) {
  async function decide(id: string, status: "active" | "denied" | "revoked") {
    await api(`/api/discipline/overrides/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    onChanged();
  }
  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <Th>Requested</Th>
          <Th>Rule</Th>
          <Th>Entity</Th>
          <Th>Reason</Th>
          <Th>Expires</Th>
          <Th>Status</Th>
          <Th>Action</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <Td>{new Date(r.requested_at).toLocaleString()}</Td>
            <Td>{r.rule_code}</Td>
            <Td>
              {r.entity_kind}/{r.entity_id}
            </Td>
            <Td>{r.reason}</Td>
            <Td>{new Date(r.expires_at).toLocaleString()}</Td>
            <Td>{r.status}</Td>
            <Td>
              {r.status === "requested" && (
                <>
                  <button
                    data-testid={`approve-override-${r.id}`}
                    onClick={() => decide(r.id, "active")}
                    style={btnPrimary}
                  >
                    Approve
                  </button>
                  <button
                    data-testid={`deny-override-${r.id}`}
                    onClick={() => decide(r.id, "denied")}
                    style={btnSecondary}
                  >
                    Deny
                  </button>
                </>
              )}
              {r.status === "active" && (
                <button
                  data-testid={`revoke-override-${r.id}`}
                  onClick={() => decide(r.id, "revoked")}
                  style={btnSecondary}
                >
                  Revoke
                </button>
              )}
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FollowupsTable({ rows }: { rows: any[] }) {
  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <Th>Rep</Th>
          <Th>Rule</Th>
          <Th>Entity</Th>
          <Th>Due</Th>
          <Th>Escalate</Th>
          <Th>Status</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <Td>{r.user_id?.slice(0, 8) ?? "—"}</Td>
            <Td>{r.rule_code}</Td>
            <Td>
              {r.entity_kind}/{r.entity_id}
            </Td>
            <Td>{new Date(r.due_at).toLocaleString()}</Td>
            <Td>{new Date(r.escalate_to_manager_at).toLocaleString()}</Td>
            <Td>{r.status}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  background: "#fff",
  border: "1px solid #ddd",
};

const Th = (props: { children: any }) => (
  <th
    style={{
      textAlign: "left",
      padding: 8,
      background: "#0B0B0F",
      color: "#FAFAF7",
      fontWeight: 600,
    }}
  >
    {props.children}
  </th>
);
const Td = (props: { children: any }) => (
  <td style={{ padding: 8, borderBottom: "1px solid #eee", verticalAlign: "top" }}>
    {props.children}
  </td>
);
const btnPrimary: React.CSSProperties = {
  background: "#0B0B0F",
  color: "#FAFAF7",
  border: 0,
  padding: "4px 8px",
  borderRadius: 4,
  cursor: "pointer",
  marginRight: 4,
};
const btnSecondary: React.CSSProperties = {
  background: "#fff",
  color: "#0B0B0F",
  border: "1px solid #0B0B0F",
  padding: "4px 8px",
  borderRadius: 4,
  cursor: "pointer",
};

function Loading() {
  return <div style={{ padding: 16, color: "#666" }}>Loading.</div>;
}
function Empty({ message }: { message: string }) {
  return (
    <div data-testid="empty-state" style={{ padding: 16, color: "#666" }}>
      {message}
    </div>
  );
}
function ErrorRow({ message }: { message: string }) {
  return (
    <OperatorShell breadcrumbs={[{ label: "Discipline" }]} pageTitle="Discipline" pageMeta="Manager discipline panel">
      {message}
    </OperatorShell>
  );
}
